import React, { useState, useEffect, useContext } from 'react';
import { segmentiULokalno, oznakaZone, opsegZaUpit, uOpsegu } from './vreme';
import { useTranslation } from 'react-i18next';
import { AuthContext } from '../../Header/auth - context';
import './AktivnostiDijagram.css';

const TYPE_META = {
    'break/rest':   { color: '#F5A623', key: 'rest',      h: 14 },
    'availability': { color: '#F7DC12', key: 'available', h: 45 },
    'work':         { color: '#1E56D6', key: 'work',      h: 62 },
    'driving':      { color: '#2E9E2E', key: 'drive',     h: 76 },
    'unknown':      { color: '#E53935', key: 'unknown',   h: 100 },
};
const metaFor = (t) => TYPE_META[t] || TYPE_META.unknown;
const LEGEND = ['rest', 'available', 'work', 'drive', 'unknown'];
const LEGEND_COLOR = { rest: '#F5A623', available: '#F7DC12', work: '#1E56D6', drive: '#2E9E2E', unknown: '#E53935' };

const hhmm = (m) => (m == null ? '—'
    : `${String(Math.floor(m / 60)).padStart(2, '0')}:${String(Math.round(m % 60)).padStart(2, '0')}`);

const fmtDate = (v) => {
    if (!v) return '';
    const s = String(v).substring(0, 10).split('-');
    return s.length === 3 ? `${Number(s[2])}.${Number(s[1])}.${s[0]}.` : String(v);
};

/* Dijagram aktivnosti iz jedinice vozila — dve trake: vozač (slot 0) i suvozač (slot 1). */
const VozilaDijagram = ({ dateFrom, dateTo, voziloId }) => {
    const { t } = useTranslation();
    const auth = useContext(AuthContext);
    const [dani, setDani] = useState([]);
    const [otvoren, setOtvoren] = useState(null);

    const toISO = (d) => (d instanceof Date
        ? `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
        : (d || ''));

    useEffect(() => {
        fetch(process.env.REACT_APP_BACKEND_URL + '/tacho/vehicleActivities', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Authorization: 'Bearer ' + auth.token },
            body: JSON.stringify({ ...opsegZaUpit(toISO(dateFrom), toISO(dateTo)), voziloId: voziloId || null }),
        })
            .then(r => (r.ok ? r.json() : []))
            .then(rows => {
                const mapa = new Map();
                /* Upit je širi za jedan dan sa svake strane — lokalni dan zahvata
                   dva UTC dana, pa bez toga fali prva 2h prvog i poslednja 2h
                   zadnjeg dana. Prikaz se vraća na traženi opseg. */
                const od = toISO(dateFrom), doo = toISO(dateTo);
                /* VU zapisi su u UTC-u; dijagram prikazuje zidni sat, isto kao
                   kontrolni izveštaj. Segment koji pređe lokalnu ponoć se seče. */
                segmentiULokalno(Array.isArray(rows) ? rows : [])
                  .filter(r => uOpsegu(String(r.ActivityDate).substring(0, 10), od, doo))
                  .forEach(r => {
                    const kljuc = `${r.VoziloId}|${String(r.ActivityDate).substring(0, 10)}`;
                    if (!mapa.has(kljuc)) {
                        mapa.set(kljuc, {
                            kljuc,
                            datum: String(r.ActivityDate).substring(0, 10),
                            registracija: r.Registracija,
                            odometar: r.OdometerMidnight,
                            gen: r.Gen,
                            kartice: r.Kartice || [],
                            segmenti: [],
                        });
                    }
                    mapa.get(kljuc).segmenti.push(r);
                });
                setDani([...mapa.values()].sort((a, b) => (a.datum < b.datum ? 1 : -1)));
            })
            .catch(() => setDani([]));
    }, [dateFrom, dateTo, voziloId, auth.token]);

    if (!voziloId) return <div className="ad-empty">{t('taho.izaberiJednoVozilo')}</div>;
    if (dani.length === 0) return <div className="ad-empty">{t('taho.nemaAktivnosti')}</div>;

    const hours = Array.from({ length: 25 }, (_, i) => i);

    const traka = (segmenti, slot) => (
        <div className="ad-plot" style={{ height: 80 }}>
            {segmenti.filter(s => s.Slot === slot).map((s, i) => {
                const meta = metaFor(s.ActivityType);
                return (
                    <div key={i} className="ad-seg"
                        style={{
                            left: `${(s.StartMinute / 1440) * 100}%`,
                            width: `${(s.Duration / 1440) * 100}%`,
                            height: `${meta.h}%`,
                            background: meta.color,
                        }}
                        title={`${hhmm(s.StartMinute)} – ${hhmm(s.StartMinute + s.Duration)} · ${t(`taho.leg_${meta.key}`)}`} />
                );
            })}
            {hours.map(h => <div key={h} className="ad-tick" style={{ left: `${(h / 24) * 100}%` }} />)}
        </div>
    );

    return (
        <div className="ad-root">
            {dani.map(d => {
                const imaSuvozaca = d.segmenti.some(s => s.Slot === 1);
                return (
                    <div key={d.kljuc} className="ad-day">
                        <div className="ad-day-title">{d.registracija || '—'}</div>
                        <div className="ad-day-meta">
                            <span><b>{t('taho.datum')}:</b> {fmtDate(d.datum)}</span>
                            {d.odometar != null && <span><b>{t('taho.odometarPonoc')}:</b> {d.odometar} km</span>}
                            <span><b>Gen:</b> {d.gen === 1 ? '1' : '2'}</span>
                            {d.kartice.length > 0 && <span><b>{t('taho.kartice')}:</b> {d.kartice.length}</span>}
                        </div>

                        <div className="ad-chart-row">
                            <div className="ad-chart-wrap">
                                <div style={{ fontSize: '.78rem', fontWeight: 600 }}>{t('taho.vozac')}</div>
                                {traka(d.segmenti, 0)}
                                {imaSuvozaca && (
                                    <>
                                        <div style={{ fontSize: '.78rem', fontWeight: 600, marginTop: 6 }}>{t('taho.suvozac')}</div>
                                        {traka(d.segmenti, 1)}
                                    </>
                                )}
                                <div className="ad-axis">
                                    {hours.map(h => <span key={h} className="ad-hour" style={{ left: `${(h / 24) * 100}%` }}>{h}</span>)}
                                </div>
                                <div className="ad-axis-row">
                                    <span className="ad-axis-date">{fmtDate(d.datum)}</span>
                                    <span className="ad-axis-label">{t('taho.vremeLokalno', { zona: oznakaZone() })}</span>
                                    <span />
                                </div>
                            </div>
                            <div className="ad-legend">
                                {LEGEND.map(k => (
                                    <div key={k} className="ad-legend-item">
                                        <span className="ad-legend-box" style={{ background: LEGEND_COLOR[k] }} />
                                        <span>{t(`taho.leg_${k}`)}</span>
                                    </div>
                                ))}
                            </div>
                        </div>

                        <div className="ad-links">
                            <button type="button" className="ad-link"
                                onClick={() => setOtvoren(otvoren === d.kljuc ? null : d.kljuc)}>
                                {otvoren === d.kljuc ? t('taho.sakrijDetalje') : t('taho.prikaziDetalje')}
                            </button>
                        </div>

                        {otvoren === d.kljuc && d.kartice.length > 0 && (
                            <table className="ad-detail-table">
                                <thead>
                                    <tr>
                                        <th>{t('taho.vozac')}</th>
                                        <th>{t('mk.kartica')}</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {d.kartice.map((k, i) => (
                                        <tr key={i}>
                                            <td>{[k.Prezime, k.Ime].filter(Boolean).join(' ') || '—'}</td>
                                            <td>{k.BrojKartice || '—'}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        )}
                    </div>
                );
            })}
        </div>
    );
};

export default VozilaDijagram;
